import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { RotateCcw, Trophy, Clock } from 'lucide-react';

interface Card {
  id: number;
  symbol: string;
  isFlipped: boolean;
  isMatched: boolean;
}

interface MemoryMatchProps {
  onClose?: () => void;
}

export const MemoryMatch: React.FC<MemoryMatchProps> = ({ onClose }) => {
  const [cards, setCards] = useState<Card[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [matches, setMatches] = useState(0);
  const [moves, setMoves] = useState(0);
  const [timer, setTimer] = useState(0);
  const [gameWon, setGameWon] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);

  const symbols = ['🎮', '🎯', '🎲', '🃏', '🎪', '🎨', '🎭', '🎸'];
  const totalPairs = 8;

  useEffect(() => {
    initializeGame();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (gameStarted && !gameWon) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameStarted, gameWon]);

  useEffect(() => {
    if (matches === totalPairs && !gameWon) {
      setGameWon(true);
      setGameStarted(false);
    }
  }, [matches, gameWon]);

  const initializeGame = () => {
    // Create pairs of cards
    const cardPairs = symbols.flatMap((symbol, index) => [
      { id: index * 2, symbol, isFlipped: false, isMatched: false },
      { id: index * 2 + 1, symbol, isFlipped: false, isMatched: false }
    ]);

    // Shuffle the cards
    const shuffledCards = shuffleArray(cardPairs);
    
    setCards(shuffledCards);
    setFlippedCards([]);
    setMatches(0);
    setMoves(0);
    setTimer(0);
    setGameWon(false);
    setGameStarted(false);
  };

  const shuffleArray = <T,>(array: T[]): T[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const handleCardClick = (cardId: number) => {
    if (!gameStarted) {
      setGameStarted(true);
    }

    const card = cards.find(c => c.id === cardId);
    if (!card || card.isFlipped || card.isMatched || flippedCards.length >= 2) {
      return;
    }

    const newFlippedCards = [...flippedCards, cardId];
    setFlippedCards(newFlippedCards);

    // Update card to show it's flipped
    setCards(prevCards =>
      prevCards.map(c =>
        c.id === cardId ? { ...c, isFlipped: true } : c
      )
    );

    if (newFlippedCards.length === 2) {
      setMoves(prev => prev + 1);
      
      const [firstId, secondId] = newFlippedCards;
      const firstCard = cards.find(c => c.id === firstId);
      const secondCard = cards.find(c => c.id === secondId);

      if (firstCard && secondCard && firstCard.symbol === secondCard.symbol) {
        // Match found
        setTimeout(() => {
          setCards(prevCards =>
            prevCards.map(c =>
              c.id === firstId || c.id === secondId
                ? { ...c, isMatched: true }
                : c
            )
          );
          setMatches(prev => prev + 1);
          setFlippedCards([]);
        }, 500);
      } else {
        // No match - flip cards back
        setTimeout(() => {
          setCards(prevCards =>
            prevCards.map(c =>
              c.id === firstId || c.id === secondId
                ? { ...c, isFlipped: false }
                : c
            )
          );
          setFlippedCards([]);
        }, 1000);
      }
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getScoreRating = () => {
    if (moves <= 12) return { text: 'Perfect!', color: 'text-green-500' };
    if (moves <= 16) return { text: 'Excellent!', color: 'text-blue-500' };
    if (moves <= 20) return { text: 'Good!', color: 'text-yellow-500' };
    return { text: 'Keep trying!', color: 'text-gray-500' };
  };

  return (
    <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-700 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 bg-black/30 rounded-lg p-3">
        <div className="flex items-center gap-4">
          <h2 className="text-white font-bold text-lg">Memory Match</h2>
          <div className="flex items-center gap-4 text-white text-sm">
            <div className="flex items-center gap-1">
              <Clock size={16} />
              {formatTime(timer)}
            </div>
            <div>Moves: {moves}</div>
            <div>Matches: {matches}/{totalPairs}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={initializeGame}
            className="bg-white/10 text-white border-white/20 hover:bg-white/20"
          >
            <RotateCcw size={16} className="mr-1" />
            New Game
          </Button>
          {onClose && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            >
              Close
            </Button>
          )}
        </div>
      </div>

      {/* Game Area */}
      <div className="flex justify-center">
        <div className="grid grid-cols-4 gap-3 p-6 bg-black/20 rounded-xl">
          {cards.map((card) => (
            <div
              key={card.id}
              className={`
                w-20 h-20 rounded-lg cursor-pointer transition-all duration-300 transform hover:scale-105
                flex items-center justify-center text-3xl font-bold border-2
                ${card.isMatched 
                  ? 'bg-green-500 border-green-400 scale-95 opacity-80' 
                  : card.isFlipped 
                    ? 'bg-white border-gray-300 shadow-lg' 
                    : 'bg-gradient-to-br from-blue-500 to-purple-600 border-blue-400 shadow-md hover:shadow-lg'
                }
              `}
              onClick={() => handleCardClick(card.id)}
            >
              {card.isFlipped || card.isMatched ? (
                <span className={card.isMatched ? 'text-white' : 'text-gray-800'}>
                  {card.symbol}
                </span>
              ) : (
                <div className="w-8 h-8 bg-white/20 rounded border border-white/30"></div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Win Message */}
      {gameWon && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 text-center max-w-md">
            <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">Congratulations!</h3>
            <div className="space-y-2 text-gray-600 mb-6">
              <p>You completed the puzzle!</p>
              <p>Time: {formatTime(timer)}</p>
              <p>Moves: {moves}</p>
              <p className={`font-semibold ${getScoreRating().color}`}>
                {getScoreRating().text}
              </p>
            </div>
            <Button onClick={initializeGame} className="mr-2">
              Play Again
            </Button>
            {onClose && (
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-6 bg-black/30 rounded-lg p-4 text-white max-w-2xl mx-auto">
        <h3 className="font-semibold mb-2">How to Play:</h3>
        <ul className="text-sm space-y-1 text-gray-200">
          <li>• Click cards to flip them over and reveal the symbols</li>
          <li>• Find pairs of matching symbols</li>
          <li>• When you find a match, both cards stay revealed</li>
          <li>• Try to match all pairs in as few moves as possible</li>
          <li>• Challenge yourself to beat your best time!</li>
        </ul>
        
        <div className="mt-4 text-xs text-gray-300">
          <h4 className="font-semibold mb-1">Scoring:</h4>
          <div className="grid grid-cols-2 gap-2">
            <div>Perfect: ≤12 moves</div>
            <div>Excellent: ≤16 moves</div>
            <div>Good: ≤20 moves</div>
            <div>Keep trying: {'>'}20 moves</div>
          </div>
        </div>
      </div>
    </div>
  );
};